<template>
  <div class="xpage">
    <div class="xpage__inner">
      <button type="button" class="xback" @click="goBack">
        <i class="mdi mdi-arrow-left" aria-hidden="true"></i>
        {{ $t("exercisePage.back") }}
      </button>

      <page-skeleton v-if="loading" variant="sheet" class="mt-4" />

      <div v-else-if="error" class="xstate">
        <p class="xstate__text">{{ $t(error) }}</p>
        <button type="button" class="xbtn xbtn--ghost" @click="load">
          {{ $t("common.tryAgain") }}
        </button>
      </div>

      <template v-else-if="view">
        <!-- Cabecalho da folha -->
        <header class="xhead">
          <p class="xhead__set">{{ view.exercise.setTitle }}</p>
          <div class="xhead__row">
            <h1 class="xhead__title">
              {{ $t("exercisePage.title", { n: view.exercise.order + 1 }) }}
              <span class="xhead__of">{{
                $t("exercisePage.of", {
                  total: view.exercise.siblings.length,
                })
              }}</span>
            </h1>
            <span
              v-if="view.exercise.difficulty"
              class="xdiff"
              :title="
                $t('exercisePage.difficultyTitle', {
                  level: view.exercise.difficulty,
                })
              "
            >
              <span
                v-for="n in 3"
                :key="n"
                class="xdiff__dot"
                :class="{ 'is-on': n <= view.exercise.difficulty }"
              ></span>
              <span class="xdiff__label">{{ difficultyLabel }}</span>
            </span>
          </div>
          <p v-if="view.exercise.prompt" class="xhead__prompt">
            {{ view.exercise.prompt }}
          </p>
          <p v-if="view.exercise.translation" class="xhead__translation">
            {{ view.exercise.translation }}
          </p>
          <exercise-media :media="view.exercise.media" />
        </header>

        <!-- Resultado, depois de finalizar -->
        <section v-if="finished" class="xresult" aria-live="polite">
          <score-mark
            :correct="view.attempt.correctItems"
            :total="view.attempt.totalItems"
            :animate="justFinished"
          />
          <div class="xresult__text">
            <p class="xresult__lead">{{ resultLead }}</p>
            <p class="xresult__hint">
              <template v-if="wrongCount">
                {{ $t("exercisePage.wrongHint") }}
              </template>
              <template v-else>{{ $t("exercisePage.noCorrections") }}</template>
            </p>
          </div>
        </section>

        <!-- A folha -->
        <section class="xpaper">
          <exercise-sheet
            v-model="responses"
            :exercise="view.exercise"
            :mode="finished ? 'review' : 'answer'"
          />
        </section>

        <!-- Comentario de quem corrige -->
        <section v-if="finished && view.feedback" class="xfeedback">
          <p class="xfeedback__who">
            {{
              $t("exercisePage.commented", {
                name: view.feedback.author || $t("sticky.yourTeacher"),
              })
            }}
          </p>
          <p class="xfeedback__text">{{ view.feedback.text }}</p>
        </section>
        <!-- Retorno de 16/10: o professor pode so marcar como revisado. -->
        <p
          v-else-if="finished && view.attempt.reviewedAt"
          class="xfeedback-wait xfeedback-wait--done"
        >
          <i class="mdi mdi-check-circle" aria-hidden="true"></i>
          {{
            $t("exercisePage.reviewed", {
              name: view.attempt.reviewedBy || $t("sticky.yourTeacher"),
            })
          }}
        </p>
        <p v-else-if="finished" class="xfeedback-wait">
          {{ $t("exercisePage.feedbackWait") }}
        </p>

        <nav v-if="finished && nextId" class="xnext">
          <router-link :to="exerciseLink(nextId)" class="xbtn xbtn--primary">
            {{ $t("exercisePage.next") }}
            <i class="mdi mdi-arrow-right" aria-hidden="true"></i>
          </router-link>
        </nav>
      </template>
    </div>

    <!-- Barra fixa de entrega -->
    <footer v-if="view && !finished" class="xbar">
      <div class="xbar__inner">
        <div class="xbar__progress">
          <span class="xbar__count">
            {{
              $t("exercisePage.answered", {
                done: answeredCount,
                total: totalCount,
              })
            }}
          </span>
          <span class="xbar__track" aria-hidden="true">
            <span class="xbar__fill" :style="{ width: progressWidth }"></span>
          </span>
          <span class="xbar__saved" aria-live="polite">{{ saveLabel }}</span>
        </div>
        <button
          type="button"
          class="xbtn xbtn--primary"
          :disabled="finishing"
          @click="askFinish"
        >
          {{
            finishing ? $t("exercisePage.grading") : $t("exercisePage.finish")
          }}
        </button>
      </div>
    </footer>

    <v-dialog v-model="blankDialog" max-width="440">
      <div class="xdialog" role="alertdialog" aria-labelledby="xdialog-title">
        <h2 id="xdialog-title" class="xdialog__title">
          {{
            $tc("exercisePage.blankTitle", blankLetters.length, {
              count: blankLetters.length,
            })
          }}
        </h2>
        <i18n
          path="exercisePage.blankText"
          :plural="blankLetters.length"
          tag="p"
          class="xdialog__text"
        >
          <template #letters>
            <b>{{ blankLetters.join(", ") }}</b>
          </template>
        </i18n>
        <div class="xdialog__actions">
          <button
            type="button"
            class="xbtn xbtn--ghost"
            @click="blankDialog = false"
          >
            {{ $t("exercisePage.goFill") }}
          </button>
          <button type="button" class="xbtn xbtn--primary" @click="finish">
            {{ $t("exercisePage.finishAnyway") }}
          </button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import customerApi from "@/shared/pitchCustomerApi";
import partnerApi from "@/shared/pitchPartnerApi";
import ExerciseSheet from "@/components/exercises/ExerciseSheet.vue";
import ExerciseMedia from "@/components/exercises/ExerciseMedia.vue";
import ScoreMark from "@/components/exercises/ScoreMark.vue";

const DRAFT_DELAY_MS = 900;
const DIFFICULTY = {
  1: "exercisePage.easy",
  2: "exercisePage.medium",
  3: "exercisePage.hard",
};

const filled = (value) => String(value || "").trim().length > 0;

// Retorno de 22/10: nos treinamentos o professor faz o exercício pela mesma
// página, com a API dele. Mesmos três passos: abrir, rascunho e finalizar.
const trainingApi = {
  getExercise: (id) => partnerApi.getTrainingExercise(id),
  saveExerciseDraft: (id, responses) =>
    partnerApi.saveTrainingExerciseDraft(id, responses),
  finishExercise: (id, responses) =>
    partnerApi.finishTrainingExercise(id, responses),
};

export default {
  name: "ExercisePage",
  components: { ExerciseSheet, ExerciseMedia, ScoreMark },

  props: {
    // customer (aula do aluno) | partner (treinamento do professor)
    profile: { type: String, default: "customer" },
  },

  data() {
    return {
      view: null,
      responses: {},
      loading: true,
      error: null,
      finishing: false,
      justFinished: false,
      blankDialog: false,
      saveState: "idle",
      timer: null,
      dirty: false,
      // Pagina de onde o aluno veio (a aula). Passar de um exercicio para o
      // proximo reaproveita o componente, entao o valor se mantem.
      returnTo: null,
    };
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (from.matched.length && from.name !== to.name) {
        vm.returnTo = from.fullPath;
      }
    });
  },

  computed: {
    api() {
      return this.profile === "partner" ? trainingApi : customerApi;
    },
    finished() {
      return this.view?.attempt?.status === "FINISHED";
    },
    totalCount() {
      return this.view?.exercise?.items?.length || 0;
    },
    answeredCount() {
      return (this.view?.exercise?.items || []).filter((item) =>
        this.isAnswered(item)
      ).length;
    },
    progressWidth() {
      return this.totalCount
        ? `${Math.round((this.answeredCount / this.totalCount) * 100)}%`
        : "0%";
    },
    blankLetters() {
      return (this.view?.exercise?.items || [])
        .map((item, index) => ({
          item,
          letter: String.fromCharCode(97 + index),
        }))
        .filter(({ item }) => !this.isAnswered(item))
        .map(({ letter }) => letter);
    },
    wrongCount() {
      return (this.view?.exercise?.items || []).filter(
        (item) => item.result && !item.result.correct
      ).length;
    },
    resultLead() {
      const { correctItems, totalItems, answeredItems } = this.view.attempt;
      const blank = totalItems - answeredItems;
      let lead =
        correctItems === totalItems
          ? this.$t("exercisePage.allRight", { total: totalItems })
          : this.$t("exercisePage.someRight", {
              correct: correctItems,
              total: totalItems,
            });
      if (blank > 0) {
        lead +=
          blank === 1
            ? this.$t("exercisePage.blankOne")
            : this.$t("exercisePage.blankMany", { count: blank });
      }
      return lead;
    },
    difficultyLabel() {
      const key = DIFFICULTY[this.view?.exercise?.difficulty];
      return key ? this.$t(key) : "";
    },
    saveLabel() {
      if (this.saveState === "saving") return this.$t("exercisePage.saving");
      if (this.saveState === "saved") return this.$t("exercisePage.saved");
      if (this.saveState === "error") return this.$t("exercisePage.saveError");
      return "";
    },
    nextId() {
      const siblings = this.view?.exercise?.siblings || [];
      const index = siblings.indexOf(this.view?.exercise?.id);
      return index >= 0 ? siblings[index + 1] || null : null;
    },
  },

  watch: {
    "$route.params.id"() {
      this.load();
    },
    responses: {
      deep: true,
      handler() {
        if (!this.dirty || this.finished) return;
        clearTimeout(this.timer);
        this.timer = setTimeout(this.saveDraft, DRAFT_DELAY_MS);
      },
    },
  },

  mounted() {
    this.load();
  },

  beforeDestroy() {
    clearTimeout(this.timer);
    // Sai da pagina com resposta nao salva: manda o rascunho sem esperar.
    if (this.dirty && !this.finished && this.saveState !== "saved") {
      this.saveDraft();
    }
  },

  methods: {
    exerciseLink(id) {
      return this.profile === "partner"
        ? `/connect/professor/exercicios/treinamento/${id}`
        : `/connect/aluno/exercicios/${id}`;
    },

    // Retorno de 16/10: o history.back() voltava para o exercicio anterior
    // quando o aluno tinha passado por "Proximo exercicio".
    goBack() {
      this.$router.push(
        this.returnTo ||
          (this.profile === "partner"
            ? "/connect/professor/treinamentos"
            : "/connect/aluno/curso")
      );
    },

    load() {
      this.loading = true;
      this.error = null;
      this.dirty = false;
      this.justFinished = false;
      this.api
        .getExercise(this.$route.params.id)
        .then((res) => {
          this.view = res.data?.data || null;
          const responses = {};
          (this.view?.exercise?.items || []).forEach((item) => {
            if (item.response) responses[item.id] = item.response;
          });
          this.responses = responses;
          this.$nextTick(() => {
            this.dirty = true;
          });
        })
        .catch((err) => {
          this.error =
            err?.response?.status === 404
              ? "exercisePage.notAvailable"
              : "exercisePage.openFailed";
        })
        .finally(() => {
          this.loading = false;
        });
    },

    isAnswered(item) {
      const response = this.responses[item.id] || {};
      if (response.optionId) return true;
      return [
        ...(response.values || []),
        ...(response.optionIds || []),
        ...(response.sequence || []),
      ].some(filled);
    },

    saveDraft() {
      if (this.finished) return Promise.resolve();
      this.saveState = "saving";
      return this.api
        .saveExerciseDraft(this.view.exercise.id, this.responses)
        .then(() => {
          this.saveState = "saved";
        })
        .catch(() => {
          this.saveState = "error";
        });
    },

    askFinish() {
      if (this.blankLetters.length) {
        this.blankDialog = true;
        return;
      }
      this.finish();
    },

    finish() {
      this.blankDialog = false;
      clearTimeout(this.timer);
      this.finishing = true;
      this.api
        .finishExercise(this.view.exercise.id, this.responses)
        .then((res) => {
          this.view = res.data?.data || this.view;
          this.justFinished = true;
          window.scrollTo({ top: 0, behavior: "smooth" });
        })
        .catch((err) => {
          this.$root.$emit("display-alert", {
            text:
              err?.response?.data?.message ||
              this.$t("exercisePage.finishFailed"),
            type: 1,
          });
        })
        .finally(() => {
          this.finishing = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
/* .xdialog repete as variaveis: o v-dialog e renderizado fora de .xpage e,
   sem elas, o botao "Finalizar assim mesmo" ficava sem fundo, com o texto
   branco invisivel ate o hover (retorno de 18/10). */
.xpage,
.xdialog {
  --ink: var(--dk-ink, #2b2540);
  --muted: var(--dk-muted, #7a7590);
  --wash: var(--dk-wash, #f6f2fb);
  --rule: var(--dk-line, #ece6f5);
  /* Retorno de 16/10: o aluno escreve na cor dele (rosa) e a caneta do
     professor e roxa. */
  --fill: var(--dk-pink, #e8276f);
  --pen: var(--dk-grape, #a826e1);
}

.xpage {
  min-height: 100%;
  background: var(--wash);
  padding: clamp(20px, 4vw, 44px) 16px 140px;
  color: var(--ink);
}

.xpage__inner {
  max-width: 760px;
  margin: 0 auto;
}

.xback {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: none;
  padding: 6px 0;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  color: var(--muted);
  cursor: pointer;
}

.xback:hover,
.xback:focus-visible {
  color: var(--ink);
}

.xstate {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 80px 0;
}

.xstate__text {
  margin: 0;
  color: var(--muted);
  text-align: center;
}

/* --- cabecalho ------------------------------------------------------- */
.xhead {
  margin: 18px 0 22px;
}

.xhead__set {
  margin: 0 0 6px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--fill);
}

.xhead__row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px 16px;
}

.xhead__title {
  margin: 0;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: clamp(30px, 5vw, 40px);
  line-height: 1.1;
  color: var(--ink);
}

.xhead__of {
  font-size: 0.5em;
  font-weight: 500;
  color: var(--muted);
}

.xdiff {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.xdiff__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--dk-line, #ddd5ea);
}

.xdiff__dot.is-on {
  background: var(--fill);
}

.xdiff__label {
  margin-left: 4px;
  font-size: 12.5px;
  color: var(--muted);
}

.xhead__prompt {
  margin: 16px 0 0;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.5;
}

.xhead__translation {
  margin: 4px 0 0;
  font-size: 14.5px;
  color: var(--muted);
}

/* --- resultado --------------------------------------------------------- */
.xresult {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 18px;
  padding: 18px 22px;
  border-radius: 16px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--rule);
}

.xresult__lead {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.xresult__hint {
  margin: 4px 0 0;
  font-size: 14px;
  color: var(--muted);
}

/* --- folha ------------------------------------------------------------ */
.xpaper {
  padding: 8px clamp(18px, 4vw, 36px);
  border-radius: 16px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--rule);
  box-shadow: 0 1px 0 var(--dk-line, #e6def2),
    0 12px 32px -24px rgba(43, 37, 64, 0.35);
}

/* --- comentario --------------------------------------------------------- */
.xfeedback {
  margin-top: 18px;
  padding: 18px 22px;
  border-radius: 16px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--dk-grape-wash, #e3cdf2);
  border-left: 4px solid var(--pen);
}

.xfeedback__who {
  margin: 0 0 6px;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 15px;
  line-height: 1.3;
  color: var(--pen);
}

.xfeedback__text {
  margin: 0;
  font-size: 15.5px;
  line-height: 1.6;
  white-space: pre-line;
}

.xfeedback-wait {
  margin: 16px 4px 0;
  font-size: 13.5px;
  color: var(--muted);
}

.xfeedback-wait--done {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--dk-teal, #00a379);
  font-weight: 600;
}

.xnext {
  display: flex;
  justify-content: flex-end;
  margin-top: 22px;
}

/* --- botoes ------------------------------------------------------------ */
.xbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 46px;
  padding: 0 22px;
  border-radius: 999px;
  border: 1.5px solid transparent;
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.15s ease, opacity 0.15s ease;
}

.xbtn:focus-visible {
  outline: 3px solid rgba(232, 39, 111, 0.35);
  outline-offset: 2px;
}

.xbtn--primary {
  background: var(--fill);
  color: #fff;
}

/* Rosa mais escuro: o roxo e a cor do professor. */
.xbtn--primary:hover {
  background: var(--dk-pink-deep, #c81f5d);
}

.xbtn--primary:disabled {
  opacity: 0.6;
  cursor: progress;
}

.xbtn--ghost {
  background: var(--dk-surface, #fff);
  border-color: var(--dk-line-strong, #ded7ea);
  color: var(--ink);
}

/* --- barra de entrega -------------------------------------------------- */
.xbar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(8px);
  border-top: 1px solid var(--rule);
}

.xbar__inner {
  display: flex;
  align-items: center;
  gap: 16px;
  max-width: 760px;
  margin: 0 auto;
}

.xbar__progress {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 4px 12px;
  flex: 1;
  min-width: 0;
}

.xbar__count {
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
}

.xbar__track {
  height: 6px;
  border-radius: 999px;
  background: var(--dk-line, #eee8f6);
  overflow: hidden;
}

.xbar__fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--fill);
  transition: width 0.25s ease;
}

.xbar__saved {
  grid-column: 1 / -1;
  font-size: 12px;
  color: var(--muted);
  min-height: 1em;
}

/* --- dialogo ----------------------------------------------------------- */
.xdialog {
  padding: 24px;
  border-radius: 16px;
  background: var(--dk-surface, #fff);
}

.xdialog__title {
  margin: 0 0 8px;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 24px;
  color: var(--dk-ink, #2b2540);
}

.xdialog__text {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: var(--dk-ink, #2b2540);
}

.xdialog__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

@media (max-width: 600px) {
  .xresult {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .xbar__inner {
    gap: 10px;
  }

  .xbar .xbtn {
    padding: 0 16px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .xbar__fill {
    transition: none;
  }
}
</style>
